<template>
  <div>
    <b-modal
      id="modal-jackpot-history"
      hide-footer
      centered
      :title="$t('field.jackpot')"
      size="xl"
      @hide="isShow = false"
    >
      <!-- Search Container Card -->
      <n-search-container
        v-if="searchFields.length"
        :active="true"
        :show-header="false"
        :loading="loading"
        @search="search"
        @reset="reset"
      >
        <n-search-input
          ref="search"
          v-model="params"
          :fields="searchFields"
        >
          <template #period="item">
            <b-form-group
              :label-for="item.field.key"
              :label="$t(item.field.label)"
            >
              <b-form-radio-group
                v-if="item.field.type === 'radio'"
                :id="item.field.key"
                :ref="item.field.key"
                v-model="params[item.field.key]"
                buttons
                button-variant="outline-primary rounded-0"
                class="flex-wrap"
              >
                <b-form-radio
                  v-for="option in item.field.options"
                  :key="option.value"
                  :value="option.value"
                >{{ $t(option.text) }}
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </template>
        </n-search-input>
      </n-search-container>

      <!-- Table Container Card -->
      <b-card
        no-body
        class="mb-0"
      >
        <div class="m-0-5">
          <b-row>
            <b-col>
              <label>{{ $t("general.entries") }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-50 mr-1"
              />
            </b-col>
          </b-row>
        </div>
        <div>
          <n-table
            :fields="fields"
            :items="items"
            :busy="loading"
            :current-page="params.page"
            :per-page="$store.state.pagination.perPage"
            :total="total"
            @sort-changed="sortChanged"
          >
            <template #cell(amount)="data">
              {{ data.item.amount | currency(data.item.ccy) }}
            </template>
            <template #cell(jackpotType)="data">
              <span :class="`jackpot-${data.item.jackpotType}`">
                {{ $t(`prize.${data.item.jackpotType}`) }}
              </span>
            </template>
            <template #custom-foot>
              <b-tr v-if="!loading">
                <b-th colspan="2" />
                <b-th class="text-center">
                  <h5>
                    {{ totalReport.totalAmount | currency(totalReport.ccy) }}
                  </h5>
                </b-th>
                <b-th />
                <b-th />
                <b-th />
              </b-tr>
            </template>
          </n-table>
          <n-pagination
            ref="jackpotHistoryPagination"
            class="mt-2"
            :total="total"
            :per-page="$store.state.pagination.perPage"
            :page="params.page"
            @change="list"
          />
        </div>
      </b-card>
    </b-modal>
    <parley-report-detail
      ref="parleyReportHistoryDetail"
      report-id="parleyReportHistoryDetail"
    />
  </div>
</template>

<script>
/* eslint-disable prefer-destructuring */
import {
  BCard, BCol, BFormGroup, BFormRadio, BFormRadioGroup, BLink, BModal, BRow, BTh, BTr,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination.vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import NTable from '@/components/NTable.vue'
import ParleyReportDetail from '@/views/parleyReport/parleyDetail/Index.vue'
import { calculateDatesByPeriod } from '@/libs/helper'
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment'
import tableFields from './tableField'
import searchInputs from './searchInput'

const BetHistoryRepository = Repository.get('jackpotHistory')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    NPagination,
    NSearchContainer,
    NSearchInput,
    NTable,
    vSelect,
    BFormGroup,
    BFormRadioGroup,
    BFormRadio,
    BTr,
    BTh,
    BModal,
    BLink,
    ParleyReportDetail,
  },

  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        channelType: 4,
        page: 1,
        order: '',
        sort: '',
        userId: '',
        startDate: moment().format('YYYY-MM-DD'),
        toDate: moment().format('YYYY-MM-DD'),
        period: 'today',
      },
      totalReport: {},
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
  },
  watch: {
    perPage() {
      this.list()
    },
    'params.period': function (value) {
      const dates = calculateDatesByPeriod(value)
      if (dates.length) {
        this.params.startDate = dates[0]
        this.params.toDate = dates[1]
      }
    },
  },
  methods: {
    show(params) {
      this.$bvModal.show('modal-jackpot-history')
      this.isShow = true
      this.params = {
        channelType: 4,
        channelId: params.channelId,
        ...params,
      }
      this.list()
    },
    hide() {
      this.isShow = false
      this.$bvModal.hide('modal-jackpot-history')
    },
    list(page = 1) {
      this.params.page = page
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.params.order = sortBy
      this.params.sort = sortDirection
      this.getData()
    },
    search(searchText) {
      this.params.page = 1
      this.params.search = searchText
      this.$refs.jackpotHistoryPagination.reset()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.params.page = 1
      this.params.startDate = moment().format('YYYY-MM-DD')
      this.params.toDate = moment().format('YYYY-MM-DD')
      this.params.period = 'today'
      this.$refs.jackpotHistoryPagination.reset()
      this.getData()
    },
    getData() {
      this.loading = true
      BetHistoryRepository.index({
        ...this.params,
      })
        .then(response => {
          const data = response.data.data.list
          this.items = [...data]
          this.total = response.data.data.total
          this.totalReport = {}
          this.loading = false
          this.getTotal()
          this.$refs.jackpotHistoryPagination.setPaginationLabel()
        })
        .catch(() => {
          this.loading = false
        })
    },
    getTotal() {
      BetHistoryRepository.total({
        ...this.params,
      }).then(response => {
        const { data } = response.data
        this.totalReport = {
          ...data,
        }
      })
    },
    showParleyDetail(data) {
      this.$refs.parleyReportHistoryDetail.show(data)
    },
    getResults(result) {
      return result ? result.split('-') : []
    },
  },
  setup() {
    const fields = [...tableFields]
    const searchFields = [...searchInputs]
    return {
      fields,
      searchFields,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
